import { useEffect, useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { SITE } from "../config/site";

const LINES = ["IMPOSSIBLE", "TO WALK", "PAST."];
const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0.1]);

  useEffect(() => {
    videoRef.current?.play().catch(() => {});
  }, []);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-svh flex-col overflow-hidden"
    >
      <motion.div className="absolute inset-0" style={reduce ? {} : { y: bgY }} aria-hidden="true">
        {reduce ? (
          <img
            src="/images/hero-poster.jpg"
            alt=""
            className="h-[115%] w-full scale-105 object-cover"
            loading="eager"
          />
        ) : (
          <video
            ref={videoRef}
            data-testid="hero-video"
            className="h-[115%] w-full scale-105 object-cover brightness-[0.85]"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster="/images/hero-poster.jpg"
          >
            <source src="/videos/hero.webm" type="video/webm" />
            <source src="/videos/hero.mp4" type="video/mp4" />
          </video>
        )}
        <div className="absolute inset-0 bg-ink/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/75 to-ink/25" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-ink to-transparent" />
      </motion.div>

      <motion.div
        style={reduce ? {} : { opacity: fade }}
        className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-4 pb-16 pt-32 sm:px-6 lg:px-8"
      >
        <motion.p
          data-testid="hero-label"
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
          className="mb-8 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.35em] text-acid sm:text-xs"
        >
          <span className="h-px w-8 bg-acid" aria-hidden="true" />
          Creative Production for Cannabis Brands
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-display text-[10.5vw] font-bold uppercase leading-[0.95] tracking-tight [word-spacing:0.12em] text-paper sm:text-[10vw] lg:text-[7.5rem]"
        >
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={reduce ? false : { y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.05, delay: 0.25 + i * 0.13, ease: EASE }}
              >
                {line === "PAST." ? <span className="text-acid">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-copy"
          initial={reduce ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
          className="mt-8 max-w-xl text-base font-light leading-relaxed text-paper/80 sm:text-lg"
        >
          We design and build the physical experiences that make cannabis brands impossible to
          ignore—from the show floor to the retail floor.
        </motion.p>

        <motion.div
          initial={reduce ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-6"
        >
          <a
            href="#contact"
            data-testid="hero-cta-brief"
            className="group flex items-center gap-3 bg-acid px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-paper"
          >
            Bring Us the Brief
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href="#story"
            data-testid="hero-cta-why"
            className="group flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-paper/80 transition-colors duration-300 hover:text-acid"
          >
            Why Triple O
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={reduce ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.15 }}
        className="relative z-10 border-t border-white/10"
      >
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6 lg:px-8">
          <p
            data-testid="hero-location"
            className="font-mono text-[10px] uppercase tracking-[0.3em] text-paper/50 sm:text-xs"
          >
            {SITE.markets}
          </p>
          <p className="hidden items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-paper/40 sm:flex">
            Scroll
            <span className="inline-block h-px w-10 bg-paper/30" aria-hidden="true" />
          </p>
        </div>
      </motion.div>
    </section>
  );
}
